import React, { useState, useCallback, useRef } from "react";
import { FaPlus } from "react-icons/fa";
import { normalizeItemSearchText } from "../../../utils/itemSearchText";
import SerialInputPopup from "./SerialInputPopup";

/**
 * KitSection — kit buttons row + management via + menu.
 *
 * + button opens float menu: ערכה חדשה | ערוך ערכה | שנה סדר
 */
const KitSection = ({
  kits = [],
  items = [],
  inventory = [],
  cart = [],
  onAddItem,
  getRemainingQuantity,
  isChildUser = false,
  childUserPersonalNumber = null,
  onAddStockShortcut = null,
  canManageKits = false,
  onSaveKit,
  onDeleteKit,
  onReorderKits,
}) => {
  // add-to-cart state
  const [kitWarnings, setKitWarnings] = useState([]);
  const [pendingSerials, setPendingSerials] = useState(null);

  // management state
  const [plusMenuOpen, setPlusMenuOpen] = useState(false);
  const [editPickOpen, setEditPickOpen] = useState(false);
  const [editingKit, setEditingKit] = useState(null);
  const [kitNameInput, setKitNameInput] = useState("");
  const [kitItemsInput, setKitItemsInput] = useState([]);
  const [itemSearch, setItemSearch] = useState("");
  const [reorderOpen, setReorderOpen] = useState(false);
  const [dragOrder, setDragOrder] = useState([]);
  const dragItem = useRef(null);

  // ── add kit to cart ──

  const getAvailableSerials = useCallback(
    (itemDefinitionId) =>
      inventory.filter((inv) => {
        if (inv.itemDefinitionId !== itemDefinitionId || !inv.serialNumber) return false;
        if (cart.find((c) => c.serial === inv.serialNumber)) return false;
        if (isChildUser && childUserPersonalNumber) {
          const first = inv.assignments?.first?.[childUserPersonalNumber];
          const second = inv.assignments?.second?.[childUserPersonalNumber];
          return !!first && !second;
        }
        return inv.status === "available";
      }).map((inv) => inv.serialNumber),
    [inventory, cart, isChildUser, childUserPersonalNumber]
  );

  const handleAddKit = useCallback(
    (kit) => {
      const warnings = [];
      const serialItemsNeeded = [];
      kit.items.forEach((kitItem) => {
        if (kitItem.hasSerialNumber) {
          const avail = getAvailableSerials(kitItem.itemDefinitionId);
          if (avail.length === 0) {
            warnings.push(`${kitItem.itemName} — אין זמין`);
          } else {
            serialItemsNeeded.push({ ...kitItem, availableSerials: avail });
          }
        } else {
          const invItem = inventory.find((inv) => inv.itemDefinitionId === kitItem.itemDefinitionId);
          const isOrganic = invItem?.isOrganic ?? null;
          const available = getRemainingQuantity(kitItem.itemDefinitionId, isOrganic) || 0;
          const qty = Math.min(kitItem.quantity || 1, available);
          if (qty === 0) { warnings.push(`${kitItem.itemName} — אין זמין`); return; }
          if (qty < (kitItem.quantity || 1)) warnings.push(`${kitItem.itemName} — נוסף ${qty} (ביקשת ${kitItem.quantity})`);
          onAddItem({
            id: invItem?.id || `kit-${Date.now()}-${kitItem.itemDefinitionId}`,
            item: kitItem.itemName,
            serial: "",
            quantity: qty,
            note: "",
            itemDefinitionId: kitItem.itemDefinitionId,
            hasSerialNumber: false,
            isOrganic,
            availableQuantity: available,
          });
        }
      });
      setKitWarnings(warnings);
      if (serialItemsNeeded.length > 0) {
        setPendingSerials({ items: serialItemsNeeded, index: 0 });
      }
    },
    [inventory, getAvailableSerials, getRemainingQuantity, onAddItem]
  );

  const handleSerialConfirm = useCallback(
    (serial) => {
      if (!pendingSerials) return;
      const current = pendingSerials.items[pendingSerials.index];
      const invItem = inventory.find(
        (inv) => inv.itemDefinitionId === current.itemDefinitionId && inv.serialNumber === serial
      );
      onAddItem({
        id: invItem?.id || Date.now().toString(),
        item: current.itemName,
        serial,
        quantity: 1,
        note: "",
        itemDefinitionId: current.itemDefinitionId,
        hasSerialNumber: true,
        isOrganic: null,
      });
      const next = pendingSerials.index + 1;
      setPendingSerials(next < pendingSerials.items.length ? { ...pendingSerials, index: next } : null);
    },
    [pendingSerials, inventory, onAddItem]
  );

  const handleSerialSkip = useCallback(() => {
    if (!pendingSerials) return;
    const next = pendingSerials.index + 1;
    setPendingSerials(next < pendingSerials.items.length ? { ...pendingSerials, index: next } : null);
  }, [pendingSerials]);

  // ── kit editor ──

  const openNew = () => {
    setEditingKit({});
    setKitNameInput("");
    setKitItemsInput([]);
    setItemSearch("");
  };

  const openEdit = (kit) => {
    setEditingKit(kit);
    setKitNameInput(kit.kitName);
    setKitItemsInput([...(kit.items || [])]);
    setItemSearch("");
  };

  const addKitRow = (itemDef) => {
    if (!itemDef || kitItemsInput.find((r) => r.itemDefinitionId === itemDef.id)) return;
    setKitItemsInput((p) => [
      ...p,
      { itemDefinitionId: itemDef.id, itemName: itemDef.itemName, hasSerialNumber: !!itemDef.isChecked, quantity: 1 },
    ]);
    setItemSearch("");
  };

  const filteredForKit = items.filter(
    (item) =>
      normalizeItemSearchText(item.itemName).includes(
        normalizeItemSearchText(itemSearch),
      ) && !kitItemsInput.find((r) => r.itemDefinitionId === item.id),
  );

  const handleSave = async () => {
    if (!kitNameInput.trim() || kitItemsInput.length === 0) return;
    await onSaveKit(kitNameInput.trim(), kitItemsInput, editingKit.id || null);
    setEditingKit(null);
  };

  const handleDelete = async () => {
    if (!editingKit?.id) return;
    await onDeleteKit(editingKit.id);
    setEditingKit(null);
  };

  // ── drag reorder (pointer events — works on touch + mouse) ──

  const dragOrderRef = useRef([]);
  const itemRefs = useRef({});

  const openReorder = () => {
    const ids = kits.map((k) => k.id);
    setDragOrder(ids);
    dragOrderRef.current = ids;
    setReorderOpen(true);
  };

  const getIndexAtY = (clientY) => {
    let closest = -1;
    let closestDist = Infinity;
    dragOrderRef.current.forEach((id, i) => {
      const el = itemRefs.current[id];
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const mid = rect.top + rect.height / 2;
      const dist = Math.abs(clientY - mid);
      if (dist < closestDist) { closestDist = dist; closest = i; }
    });
    return closest;
  };

  const handlePointerDown = (e, id) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragItem.current = id;
  };

  const handlePointerMove = (e) => {
    if (dragItem.current === null) return;
    const fromId = dragItem.current;
    const toIdx = getIndexAtY(e.clientY);
    if (toIdx === -1) return;
    setDragOrder((prev) => {
      const fromIdx = prev.indexOf(fromId);
      if (fromIdx === -1 || fromIdx === toIdx) return prev;
      const next = [...prev];
      next.splice(fromIdx, 1);
      next.splice(toIdx, 0, fromId);
      dragOrderRef.current = next;
      return next;
    });
  };

  const handlePointerUp = () => {
    dragItem.current = null;
  };

  const saveReorder = async () => {
    await onReorderKits(dragOrderRef.current);
    setReorderOpen(false);
  };

  // ──────────────────────────────────────────

  return (
    <div className="kit-section">
      {/* Kit buttons row — clean, no per-kit controls */}
      <div className="kit-buttons-row">
        {kits.map((kit) => (
          <button
            key={kit.id}
            className="kit-btn"
            onClick={() => { setKitWarnings([]); handleAddKit(kit); }}
          >
            {kit.kitName}
          </button>
        ))}

        {canManageKits && (
          <button
            className="kit-add-new-btn"
            onClick={() => setPlusMenuOpen(true)}
            title="ניהול ערכות"
          >
            <FaPlus />
          </button>
        )}
      </div>

      {/* Warnings */}
      {kitWarnings.length > 0 && (
        <div className="kit-warnings">
          {kitWarnings.map((w, i) => <div key={i} className="kit-warning-row">{w}</div>)}
          <button className="kit-warning-close" onClick={() => setKitWarnings([])}>✕</button>
        </div>
      )}

      {/* + menu — float window */}
      {plusMenuOpen && (
        <div className="kit-editor-overlay" onClick={() => setPlusMenuOpen(false)}>
          <div className="kit-plus-float" onClick={(e) => e.stopPropagation()}>
            <div className="kit-editor-header">
              <span>ניהול ערכות</span>
              <button className="icon-btn" onClick={() => setPlusMenuOpen(false)}>✕</button>
            </div>
            <div className="kit-plus-options">
              <div className="kit-plus-option" onClick={() => { setPlusMenuOpen(false); openNew(); }}>
                ערכה חדשה
              </div>
              {kits.length > 0 && (
                <div className="kit-plus-option" onClick={() => { setPlusMenuOpen(false); setEditPickOpen(true); }}>
                  ערוך ערכה
                </div>
              )}
              {kits.length > 1 && (
                <div className="kit-plus-option" onClick={() => { setPlusMenuOpen(false); openReorder(); }}>
                  שנה סדר
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit pick — choose which kit to edit */}
      {editPickOpen && (
        <div className="kit-editor-overlay" onClick={() => setEditPickOpen(false)}>
          <div className="kit-editor" onClick={(e) => e.stopPropagation()}>
            <div className="kit-editor-header">
              <span>בחר ערכה לעריכה</span>
              <button className="icon-btn" onClick={() => setEditPickOpen(false)}>✕</button>
            </div>
            <div className="kit-pick-list">
              {kits.map((kit) => (
                <div
                  key={kit.id}
                  className="kit-pick-item"
                  onClick={() => { setEditPickOpen(false); openEdit(kit); }}
                >
                  {kit.kitName}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Kit editor — float window */}
      {editingKit !== null && (
        <div className="kit-editor-overlay" onClick={() => setEditingKit(null)}>
          <div className="kit-editor" onClick={(e) => e.stopPropagation()}>
            <div className="kit-editor-header">
              <span>{editingKit.id ? "עריכת ערכה" : "ערכה חדשה"}</span>
              <button className="icon-btn" onClick={() => setEditingKit(null)}>✕</button>
            </div>
            <input
              className="kit-name-input"
              placeholder="שם הערכה..."
              value={kitNameInput}
              onChange={(e) => setKitNameInput(e.target.value)}
            />
            <div className="kit-items-scroll">
              <table className="kit-items-table">
                <thead>
                  <tr><th>פריט</th><th>סידורי</th><th>כמות</th><th></th></tr>
                </thead>
                <tbody>
                  {kitItemsInput.map((row) => (
                    <tr key={row.itemDefinitionId}>
                      <td>{row.itemName}</td>
                      <td>{row.hasSerialNumber ? "כן" : "לא"}</td>
                      <td>
                        {!row.hasSerialNumber && (
                          <input
                            type="number" min="1" value={row.quantity}
                            onChange={(e) =>
                              setKitItemsInput((p) =>
                                p.map((r) =>
                                  r.itemDefinitionId === row.itemDefinitionId
                                    ? { ...r, quantity: parseInt(e.target.value) || 1 }
                                    : r
                                )
                              )
                            }
                            className="kit-qty-input"
                          />
                        )}
                      </td>
                      <td>
                        <button
                          className="icon-btn danger"
                          onClick={() =>
                            setKitItemsInput((p) => p.filter((r) => r.itemDefinitionId !== row.itemDefinitionId))
                          }
                        >✕</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="kit-add-item-label">הוסף פריט לערכה:</div>
            <div className="kit-add-item-row">
              <input
                className="kit-item-search"
                placeholder="הוסף פריט לערכה..."
                value={itemSearch}
                onChange={(e) => setItemSearch(e.target.value)}
              />
              {itemSearch && filteredForKit.length > 0 && (
                <div className="kit-item-dropdown">
                  {filteredForKit.slice(0, 10).map((item) => (
                    <div key={item.id} className="kit-item-option" onClick={() => addKitRow(item)}>
                      {item.itemName}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="kit-editor-footer">
              {editingKit.id && (
                <button className="btn btn-danger btn-sm" onClick={handleDelete}>מחק</button>
              )}
              <button className="btn btn-secondary btn-sm" onClick={() => setEditingKit(null)}>ביטול</button>
              <button
                className="btn btn-primary btn-sm"
                onClick={handleSave}
                disabled={!kitNameInput.trim() || kitItemsInput.length === 0}
              >
                שמור
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Drag reorder — float window */}
      {reorderOpen && (
        <div className="kit-editor-overlay" onClick={() => setReorderOpen(false)}>
          <div className="kit-editor" onClick={(e) => e.stopPropagation()}>
            <div className="kit-editor-header">
              <span>שנה סדר ערכות</span>
              <button className="icon-btn" onClick={() => setReorderOpen(false)}>✕</button>
            </div>
            <p className="kit-reorder-hint">גרור כדי לשנות את הסדר</p>
            <div className="kit-reorder-list">
              {dragOrder.map((id) => {
                const kit = kits.find((k) => k.id === id);
                if (!kit) return null;
                return (
                  <div
                    key={id}
                    ref={(el) => { itemRefs.current[id] = el; }}
                    className="kit-reorder-item"
                    onPointerDown={(e) => handlePointerDown(e, id)}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                    style={{ touchAction: "none" }}
                  >
                    <span className="kit-reorder-handle">☰</span>
                    {kit.kitName}
                  </div>
                );
              })}
            </div>
            <div className="kit-editor-footer">
              <button className="btn btn-secondary btn-sm" onClick={() => setReorderOpen(false)}>ביטול</button>
              <button className="btn btn-primary btn-sm" onClick={saveReorder}>שמור סדר</button>
            </div>
          </div>
        </div>
      )}

      {/* Serial popup for kit */}
      {pendingSerials && (
        <SerialInputPopup
          itemName={pendingSerials.items[pendingSerials.index].itemName}
          itemDefinitionId={
            pendingSerials.items[pendingSerials.index].itemDefinitionId
          }
          availableSerials={
            pendingSerials.items[pendingSerials.index].availableSerials
          }
          onConfirm={handleSerialConfirm}
          onCancel={handleSerialSkip}
          onAddStockShortcut={onAddStockShortcut}
        />
      )}
    </div>
  );
};

export default KitSection;
